import { environment } from '@env/environment';
import { AppConfig } from './app-config.token';

/**
 * Per-deployment bootstrap file, written at RELEASE time (not build time).
 * It tells the single shared client artifact where its API lives and which
 * tenant it is serving, so the client can fetch its full configuration.
 */
interface DeploymentConfig {
  apiBaseUrl: string;
  tenantLabel?: string;
}

async function fetchJson<T>(url: string): Promise<T> {
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) {
    throw new Error(`Config fetch failed: ${url} -> ${response.status}`);
  }
  return (await response.json()) as T;
}

/**
 * Resolve the runtime configuration before the application bootstraps.
 *
 *   1. Read `/config.json` from the site root (deployment-specific: API base
 *      URL + tenant). It lives in `public/` so it ships outside the hashed
 *      bundle and the release process can overwrite it per deployment.
 *   2. Fetch the tenant-resolved, secret-stripped client config from the API
 *      (`GET {apiBaseUrl}/api/config`).
 *   3. Overlay the runtime values on the build-time `environment` defaults.
 *
 * Any failure falls back to the build-time `environment`. This keeps the app
 * working throughout the migration, even before the API endpoint exists.
 */
export async function loadAppConfig(): Promise<AppConfig> {
  try {
    const deployment = await fetchJson<DeploymentConfig>('config.json');
    const remote = await fetchJson<Partial<AppConfig>>(
      `${deployment.apiBaseUrl}/api/config`,
    );
    return { ...environment, ...remote };
  } catch {
    // Migration safety net: keep running on the build-time environment.
    return environment;
  }
}
